/**
 * Uploads for video / document share.
 *
 * The problem this solves: a picked file becomes a `blob:` URL, which is a
 * handle into the presenter's own tab. Broadcasting one hands every other
 * participant a link that cannot resolve. So the file has to exist somewhere
 * the room can reach — the host backend's `/api/v1/attachments`, the same
 * store meeting chat already uploads to.
 *
 * Fetching it back is the awkward half. `/attachments/:id/raw` is guarded by
 * `requireAuth`, which reads the **Authorization header** — and a browser never
 * attaches that header to a `<video src>` or `<iframe src>` subresource
 * request. Pointing a player straight at the URL 401s.
 *
 * So each client fetches the bytes itself, with its own bearer, and makes a
 * local object URL from them. The URL on the wire is the same for everyone;
 * every participant resolves it under their own identity. Nothing is made
 * public to do it.
 *
 * Cost: the file is buffered before playback rather than streamed, so there is
 * no range-request seeking. Acceptable against the backend's 25 MiB cap; it
 * would not be for feature-length video, which wants a signed streaming URL.
 *
 * Its own module rather than importing chat's uploader — same reasoning
 * recorded in chat-attachment.api.ts: sharing the endpoint is fine, sharing a
 * module means a change to one feature can break the other.
 */

import { config } from '@/config/env'
import { roomFetch, uploadFailureMessageFor, MeetingEndedError, uploadUnreachableMessage, type FetchTransport } from './room-token'
import type { SkipNetworkErrorInit } from '@/clientWeb/components/ui/network-error-dialog'
import { checkFileForUpload } from '@/lib/upload-policy'
import { sendWithProgress, type UploadProgress } from '@/lib/send-with-progress'
import { useMeetingSessionStore } from '../store/meeting-session.store'

const API = config.apiUrl

/** Mirrors the backend's multer limit (25 MiB) so oversize is caught here with
 *  a real message instead of an opaque 413 mid-upload. */
export const MAX_SHARE_UPLOAD_BYTES = 25 * 1024 * 1024

export type UploadedShare = {
  /** The server's attachment id. Keep it if the object may be deleted later —
   *  see `deleteUploadedShare` for why the URL is not enough on S3. */
  id: string
  url: string
  name: string
  size: number
  mime: string
  /**
   * A PDF copy, for PowerPoint, Word and text files: what Present shows, since
   * a browser cannot show the original. Absent when the file needs none, or
   * the backend could not make one (it is then download-only).
   */
  preview?: { id: string; url: string }
}

/** The app (host-backend) bearer. Empty for guests, who cannot upload. */
function appToken(): string {
  try {
    const raw =
      localStorage.getItem('inmeet_auth_session') ||
      sessionStorage.getItem('inmeet_auth_session')
    if (!raw) return ''
    const parsed = JSON.parse(raw) as { session?: { accessToken?: string; expiresAt?: number } }
    const session = parsed?.session
    if (session?.expiresAt && session.expiresAt <= Date.now()) return ''
    return session?.accessToken || ''
  } catch {
    return ''
  }
}

/** Anyone admitted to the room may share; a signed-in user may share outside one
 *  (the schedule form, which has no room yet). */
export function canUploadShares(): boolean {
  return roomAccess() !== null || appToken() !== ''
}

/** Room id + token for this meeting, or null when we are not in one. */
function roomAccess(): { roomId: string; roomToken: string } | null {
  const creds = useMeetingSessionStore.getState().roomCredentials
  const roomId = creds?.channelTopic?.replace(/^room:/, '')
  if (!creds?.roomToken || !roomId) return null
  return { roomId, roomToken: creds.roomToken }
}

/** `/api/v1/attachments/<id>/raw` → `<id>`. Empty when the URL is not one. */
function attachmentIdOf(url: string): string {
  return url.match(/\/attachments\/([^/?#]+)/)?.[1] ?? ''
}

function absolutize(url: string): string {
  if (/^https?:\/\//i.test(url)) return url
  return `${API}${url.startsWith('/') ? '' : '/'}${url}`
}

// Moved to lib once meeting chat and Presence chat needed it too; re-exported
// so this module's callers and tests are unchanged.
export { sendWithProgress, type UploadProgress } from '@/lib/send-with-progress'

/**
 * Upload one file and return the URL to broadcast.
 *
 * Throws on failure — a share that silently didn't upload looks exactly like
 * one that did, right up until nobody else can see it.
 */
/**
 * Upload a file for sharing.
 *
 * `group` puts it under one storage prefix with the rest of a meeting's files,
 * so they can be swept together. It defaults to the room id, which is the
 * meeting's own uuid — so a document uploaded during a meeting is grouped
 * without any caller having to know about it. The schedule form passes its own
 * group instead, because the meeting does not exist yet when files are attached
 * there.
 *
 * `onProgress` hears how much of the file has gone, as it goes. When every
 * byte is sent the server still has work left (saving, and the PDF copy for
 * Office and text files), so a caller can tell sending from preparing.
 */
export async function uploadShareFile(
  file: File,
  group?: string,
  onProgress?: UploadProgress,
): Promise<UploadedShare> {
  // Room token first, mirroring the read path.
  //
  // Being admitted to the meeting is the right claim for putting a file into
  // it, and it is the ONLY claim a guest has — they join by link and hold no
  // app session. Requiring one told a participant standing in the meeting to
  // "sign in to share a file with the room", which reads as policy when it was
  // only this client calling the authed-only endpoint. The backend has carried
  // a room-token upload route since the download side hit the same wall.
  const room = roomAccess()
  const token = appToken()
  if (!room && !token) throw new Error('Sign in to share a file with the room.')
  // The server's policy decides what may be shared, so every picker, drop
  // zone and schedule form that reaches this function is held to it once.
  const check = await checkFileForUpload(file, 'attachment')
  if (!check.ok) throw new Error(check.message)
  if (file.size > MAX_SHARE_UPLOAD_BYTES) {
    throw new Error(`${file.name} is larger than 25 MB.`)
  }

  const form = new FormData()
  form.append('file', file)
  // Each file still gets its own uuid inside the prefix, so nothing collides.
  const resolvedGroup = group ?? room?.roomId
  if (resolvedGroup) form.append('group', resolvedGroup)

  const transport: FetchTransport = onProgress ? sendWithProgress(onProgress) : fetch

  let res: Response
  try {
    res = room
      ? await roomFetch(
          room.roomId,
          room.roomToken,
          `${API}/api/v1/meeting-chat/${encodeURIComponent(room.roomId)}/attachments`,
          { method: 'POST', body: form, skipNetworkError: true } as SkipNetworkErrorInit,
          transport,
        )
      : await transport(`${API}/api/v1/attachments`, {
          skipNetworkError: true,
          method: 'POST',
          credentials: 'include',
          // No Content-Type — the browser must set the multipart boundary.
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        } as SkipNetworkErrorInit)
  } catch (err) {
    if (err instanceof MeetingEndedError) throw err
    throw new Error(await uploadUnreachableMessage())
  }

  if (!res.ok) throw new Error(await uploadFailureMessageFor(res))

  type StoredAttachment = { id: string; mime?: string; sizeBytes?: number; filename?: string | null; url?: string }
  const body = (await res.json()) as { attachment?: StoredAttachment; preview?: StoredAttachment | null }
  const att = body.attachment
  if (!att?.id) throw new Error('Upload failed')

  // `url` is present only on the S3 backend; local disk hands back nothing and
  // the durable proxy path is used instead.
  const urlOf = (a: StoredAttachment) => (a.url ? absolutize(a.url) : `${API}/api/v1/attachments/${a.id}/raw`)
  const url = urlOf(att)
  const preview = body.preview?.id ? { id: body.preview.id, url: urlOf(body.preview) } : undefined

  return {
    // The server's id for this object. Callers that may later delete it must
    // keep this: on the S3 backend `url` is a presigned link to the bucket and
    // carries no attachment id at all, so an id recovered from the URL is
    // empty and the delete silently does nothing.
    id: att.id,
    url,
    name: att.filename || file.name,
    size: att.sizeBytes ?? file.size,
    mime: att.mime || file.type,
    ...(preview ? { preview } : {}),
  }
}

/** Does this URL sit behind the backend's bearer auth? */
export function needsAuthenticatedFetch(url: string): boolean {
  return url.includes('/api/v1/attachments/')
}

/**
 * Is this URL an object WE uploaded, rather than a link someone pasted?
 *
 * The two are handled differently by anything that embeds them. A pasted link
 * is a web page and an iframe is right for it. An uploaded object is served
 * from the store, which stamps `Content-Disposition: attachment` on everything
 * outside its inline-safe list — point an iframe at one of those and every
 * participant's browser saves the file instead of showing it.
 *
 * Matches both backends: the local-disk proxy path, and the S3 key layout
 * `meetings/<group>/<uuid>` that `uploadShareFile` writes to.
 */
export function isUploadedShareUrl(url?: string | null): boolean {
  if (!url) return false
  if (url.includes('/api/v1/attachments/')) return true
  return /\/meetings\/[^/]+\/[0-9a-f-]{16,}/i.test(url)
}

/**
 * Save a shared file to disk.
 *
 * `window.open` is not enough for an uploaded file: `/attachments/:id/raw`
 * wants an Authorization header, which a top-level navigation cannot carry, so
 * the tab would land on a 401 JSON body. Attachments are fetched with the
 * bearer and saved from the resulting blob; a public link opens normally.
 */
export async function downloadSharedFile(url: string, filename: string): Promise<void> {
  // A file shared into this meeting may open only through this viewer's own
  // link, which a navigation to its URL cannot ask for. It is fetched and saved
  // from the blob like an authed one: no tab opened after an await (blocked as
  // a popup), and a blob: URL is same-origin, so `download` names the file.
  if (!needsAuthenticatedFetch(url) && !roomShareOf(url)) {
    window.open(url, '_blank', 'noopener,noreferrer')
    return
  }

  const { objectUrl } = await fetchShareAsObjectUrl(url, undefined, filename)
  try {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = filename || 'download'
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    // The click has already started the save; the blob can go.
    URL.revokeObjectURL(objectUrl)
  }
}

/**
 * Declared types that say nothing about the file, so the filename may overrule
 * them. Mirrors the store's own list — the store decides what to SERVE a file
 * as, this decides what to RENDER it as, and they have to agree.
 *
 * `application/vnd.ms-excel` is the one that bites: a browser reports whatever
 * the OS registered for the extension, and wherever Excel owns `.csv` — the
 * default on most Windows machines — a plain CSV is uploaded under that type.
 */
const UNRELIABLE_MIMES = new Set([
  '',
  'application/octet-stream',
  'binary/octet-stream',
  'application/unknown',
  'application/force-download',
  'application/vnd.ms-excel',
  'application/csv',
  'application/x-csv',
  'text/x-csv',
  'text/comma-separated-values',
  'text/x-comma-separated-values',
])

/**
 * Extensions we are willing to name a type for.
 *
 * `html` and `svg` are absent and must stay absent. A blob: URL inherits the
 * creating page's origin, so framing one typed `text/html` would run its
 * contents as us — the exact thing the store's inline-safe list exists to
 * prevent, reintroduced on the client. Everything here is inert: a browser
 * paints it and executes nothing in it.
 */
const RENDERABLE_MIME_BY_EXT: Record<string, string> = {
  csv: 'text/csv',
  txt: 'text/plain',
  json: 'application/json',
  md: 'text/markdown',
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  bmp: 'image/bmp',
  // Shared video and audio. A blob typed as a generic download is guesswork
  // for the player: Chrome sniffs the bytes and plays anyway, Safari on iOS
  // is stricter about what it is handed.
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  mkv: 'video/x-matroska',
  ogv: 'video/ogg',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  wav: 'audio/wav',
}

/**
 * What this file should be rendered as, given what the server served and what
 * it is called.
 *
 * Only consulted when the served type carries no information — an explicit
 * `text/html` is left exactly as served and still fails the embed check.
 */
export function renderableMime(served: string, filename?: string | null): string {
  const mime = (served ?? '').toLowerCase().split(';')[0].trim()
  if (!UNRELIABLE_MIMES.has(mime)) return served
  const ext = /\.([a-z0-9]{1,5})(?:[?#]|$)/i.exec(filename ?? '')?.[1]?.toLowerCase()
  return (ext && RENDERABLE_MIME_BY_EXT[ext]) || served
}

/** Re-type the fetched bytes when the served type was a guess, and hand back a
 *  local URL for them. A blob: URL carries no `Content-Disposition`, which is
 *  what stops the browser saving the file instead of showing it. */
export function toObjectUrl(blob: Blob, filename?: string | null): { objectUrl: string; mime: string } {
  const mime = renderableMime(blob.type, filename)
  const typed = mime === blob.type ? blob : new Blob([blob], { type: mime })
  return { objectUrl: URL.createObjectURL(typed), mime }
}

/**
 * A file shared into THIS meeting, named by its unsigned store URL:
 * `…/meetings/<roomId>/<attachmentId>`, the key `uploadShareFile` writes. Null
 * for anything else: a pasted link, another meeting's file (a scheduled one
 * sits under its own group), a URL that already carries its signature, or no
 * room to ask in.
 */
function roomShareOf(url: string): { roomId: string; roomToken: string; id: string } | null {
  const room = roomAccess()
  if (!room) return null
  let u: URL
  try {
    u = new URL(url)
  } catch {
    return null
  }
  if (u.searchParams.has('Signature') || u.searchParams.has('Key-Pair-Id')) return null
  if (u.pathname.includes('/private/')) return null
  const m = u.pathname.match(/\/meetings\/([^/]+)\/([^/]+)$/)
  if (!m || decodeURIComponent(m[1]) !== room.roomId) return null
  return { ...room, id: decodeURIComponent(m[2]) }
}

/**
 * This viewer's own link to a file shared into the meeting.
 *
 * Where the CDN restricts `meetings/` (CF_RESTRICTED_PREFIXES on the backend,
 * VAPT 2026-10), the URL broadcast with a share only names the file: fetched
 * as it is, the CDN answers 403 without CORS headers, which the browser reports
 * as a CORS error and the player as "could not load". The room's route signs it
 * for whoever is admitted, so each participant asks for theirs, with their own
 * room token, at the moment of loading. Where `meetings/` is still public the
 * route hands back that public URL. Anything it will not answer keeps the URL
 * it came with, which is what loaded before.
 */
async function roomShareLink(url: string, signal?: AbortSignal): Promise<string> {
  const share = roomShareOf(url)
  if (!share) return url
  let res: Response
  try {
    res = await roomFetch(
      share.roomId,
      share.roomToken,
      `${API}/api/v1/meeting-chat/${encodeURIComponent(share.roomId)}/attachments/${encodeURIComponent(share.id)}/url`,
      // Reports its own failure (by falling back), so a dropped request here
      // must not black out the meeting with "Connection Lost".
      { signal, skipNetworkError: true } as SkipNetworkErrorInit,
    )
  } catch (err) {
    if (signal?.aborted) throw err
    return url
  }
  if (!res.ok) return url
  const body = (await res.json().catch(() => null)) as { url?: unknown } | null
  return typeof body?.url === 'string' && /^https?:\/\//i.test(body.url) ? body.url : url
}

/**
 * Fetch a shared file and return a local object URL for it.
 *
 * Used for anything WE uploaded, whether or not it needs auth. A public bucket
 * object could be framed directly, but must not be: the store stamps
 * `Content-Disposition: attachment` on everything outside its inline-safe list,
 * so pointing an iframe at one saves the file on every participant's machine
 * and leaves a blank frame behind. Fetching the bytes and re-typing them here
 * sidesteps the header entirely, and does it on the client — so it holds for
 * files that were uploaded before the store learned to type them correctly.
 *
 * None of these requests raises "Connection Lost". A failure is the caller's
 * to report — the player and the document stage say the file could not be
 * loaded and offer the download — and a CDN refusal without CORS headers fails
 * exactly like a dropped network, so one unloadable file blacked out the whole
 * meeting and told everyone to restart their router.
 *
 * Caller owns the result and must revokeObjectURL when done.
 */
export async function fetchShareAsObjectUrl(
  url: string,
  signal?: AbortSignal,
  filename?: string | null,
): Promise<{ objectUrl: string; mime: string }> {
  // A public store object carries no auth and must be fetched without any:
  // `Authorization` makes it a preflighted request, which the bucket answers
  // with a rejection rather than the file.
  // A file shared into this meeting is fetched through this viewer's own link.
  if (!needsAuthenticatedFetch(url)) {
    const res = await fetch(await roomShareLink(url, signal), {
      signal,
      skipNetworkError: true,
    } as SkipNetworkErrorInit)
    if (!res.ok) throw new Error(`Could not load the shared file (${res.status})`)
    return toObjectUrl(await res.blob(), filename)
  }

  // Room token first. Being in the meeting is the right claim for reading a
  // file shared into it, and it is the only claim a guest has — they join by
  // link and hold no app session, so requiring one told them to "sign in to
  // view this shared file" for a document sitting in the meeting they were
  // already in. A signed-in participant has both and uses this path too, so
  // there is one route to keep working rather than two.
  const room = roomAccess()
  if (room) {
    const res = await roomFetch(
      room.roomId,
      room.roomToken,
      `${API}/api/v1/meeting-chat/${encodeURIComponent(room.roomId)}/attachments/${attachmentIdOf(url)}/raw`,
      { signal, skipNetworkError: true } as SkipNetworkErrorInit,
    )
    if (res.ok) return toObjectUrl(await res.blob(), filename)
    // Fall through to the app token: an older attachment URL that carries no
    // id, or a room whose token has expired, should still work for someone
    // signed in rather than failing outright.
  }

  const token = appToken()
  if (!token) throw new Error('Could not load the shared file.')

  const res = await fetch(url, {
    credentials: 'include',
    headers: { Authorization: `Bearer ${token}` },
    signal,
    skipNetworkError: true,
  } as SkipNetworkErrorInit)
  if (!res.ok) throw new Error(`Could not load the shared file (${res.status})`)

  return toObjectUrl(await res.blob(), filename)
}

/**
 * Delete a file that was uploaded and then cancelled.
 *
 * The bytes go to storage the moment the file is picked, so dropping it from
 * the form is not enough — without this the object stays in the bucket for
 * good, belonging to nothing.
 *
 * Best effort: the row is already gone from the caller's list, and failing to
 * reach the server should not block that. It leaves an orphan for the sweep
 * rather than an error the user can do nothing about.
 */
/**
 * Delete an uploaded object.
 *
 * Pass the attachment `id` whenever you have it. Falling back to reading it
 * out of the URL only works on the local-disk backend, where the URL is
 * `/api/v1/attachments/<id>/raw`. On S3 the URL is a presigned link to the
 * bucket — no id anywhere in it — so the regex found nothing, the function
 * returned early, and the object stayed in the bucket forever while
 * disappearing from the thing that referenced it. It looked correct in local
 * development for exactly that reason.
 */
export async function deleteUploadedShare(url: string, attachmentId?: string): Promise<void> {
  const id = attachmentId || attachmentIdOf(url)
  const token = appToken()
  if (!id || !token) return
  try {
    await fetch(`${API}/api/v1/attachments/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    })
  } catch {
    /* orphan left behind rather than an error the user cannot act on */
  }
}
